import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AdminBlogCategory } from '@app/model/blog-management.model';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { Subject, Subscription } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { BlogCategoryFormComponent } from '../blog-category-form/blog-category-form.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@app/../environments/environment';

@Component({
  selector: 'app-blog-category-management',
  templateUrl: './blog-category-management.component.html',
  styleUrls: ['./blog-category-management.component.scss']
})
export class BlogCategoryManagementComponent implements OnInit, OnDestroy {
  @ViewChild('pageNumberInput', { static: false })
  pageNumberInput: ElementRef<HTMLInputElement>;

  categories: AdminBlogCategory[] = [];
  filteredCategories: AdminBlogCategory[] = [];

  searchText = '';
  visibilityFilter = 'all';
  levelFilter = 'all';

  pageIndex = 1;
  pageSize = 10;
  isEditingPage = false;

  isLoading = false;
  errorMessage = '';
  deletingCategoryId = '';

  private destroy$ = new Subject<void>();
  private categoriesSubscription = new Subscription();

  constructor(
    private blogManagementDataService: BlogManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
  }

  ngOnDestroy(): void {
    this.categoriesSubscription.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  get pagedCategories(): AdminBlogCategory[] {
    const startIndex = (this.pageIndex - 1) * this.pageSize;

    return this.filteredCategories.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredCategories.length / this.pageSize));
  }

  get visiblePages(): number[] {
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.pageIndex - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    startPage = Math.max(1, endPage - maxVisiblePages + 1);

    const pages: number[] = [];

    for (let page = startPage; page <= endPage; page++) {
      pages.push(page);
    }

    return pages;
  }

  getCategoryImageUrl(
  imagePath: string | null
): string {
  if (!imagePath) {
    return '';
  }

  if (imagePath.indexOf('http') === 0) {
    return imagePath;
  }

  return environment.backendhost + imagePath;
}

  hasChildCategories(category: AdminBlogCategory): boolean {
    return category.childCount > 0 || this.categories.some((item) => item.parentId === category.id);
  }

  openCreateCategory(): void {
    const dialogRef = this.dialog.open(BlogCategoryFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'category-form-dialog',
      data: {
        mode: 'create',
        categories: this.categories
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.created) {
          this.loadCategories();
        }
      });
  }

  loadCategories(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.categoriesSubscription.unsubscribe();

    this.categoriesSubscription = this.blogManagementDataService
      .getCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (categories) => {
          this.categories = categories;
          this.isLoading = false;
          this.applyFilters();
        },
        (error) => {
          console.error('Load blog categories failed:', error);

          this.categories = [];
          this.filteredCategories = [];
          this.isLoading = false;
          this.errorMessage = 'Không thể tải danh sách danh mục';
        }
      );
  }

  openEditCategory(category: AdminBlogCategory): void {
    const dialogRef = this.dialog.open(BlogCategoryFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'category-form-dialog',
      data: {
        mode: 'edit',
        category,
        categories: this.categories
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.updated) {
          this.loadCategories();
        }
      });
  }

  confirmDeleteCategory(category: AdminBlogCategory): void {
    if (this.hasChildCategories(category)) {
      this.toastr.error('Category còn danh mục con nên không thể xóa');

      return;
    }

    this.blogManagementDataService
      .hasPostsInCategory(category.slug)
      .pipe(take(1))
      .subscribe(
        (hasPosts) => {
          if (hasPosts) {
            this.toastr.error('Category còn bài viết nên không thể xóa');

            return;
          }

          this.openDeleteCategoryConfirmation(category);
        },
        () => {
          this.toastr.error('Không thể kiểm tra bài viết của danh mục');
        }
      );
  }

  applyFilters(): void {
    const keyword = this.searchText.trim().toLowerCase();

    this.filteredCategories = this.categories.filter((category) => {
      const matchesKeyword =
        !keyword ||
        category.name.toLowerCase().includes(keyword) ||
        category.code.toLowerCase().includes(keyword) ||
        category.slug.toLowerCase().includes(keyword);

      const matchesVisibility =
        this.visibilityFilter === 'all' ||
        (this.visibilityFilter === 'visible' && !category.isHidden) ||
        (this.visibilityFilter === 'hidden' && category.isHidden);

      const matchesLevel =
        this.levelFilter === 'all' ||
        (this.levelFilter === 'root' && !category.parentId) ||
        (this.levelFilter === 'child' && !!category.parentId);

      return matchesKeyword && matchesVisibility && matchesLevel;
    });

    this.pageIndex = 1;
    this.isEditingPage = false;
  }

  clearFilters(): void {
    this.searchText = '';
    this.visibilityFilter = 'all';
    this.levelFilter = 'all';
    this.applyFilters();
  }

  getParentName(category: AdminBlogCategory): string {
    if (!category.parentId) {
      return 'Danh mục gốc';
    }

    const parentCategory = this.categories.find((item) => item.id === category.parentId);

    return parentCategory ? parentCategory.name : 'Không xác định';
  }

  goToPage(page: number): void {
    if (!Number.isInteger(page) || page < 1 || page > this.totalPages) {
      return;
    }

    this.pageIndex = page;
    this.isEditingPage = false;
  }

  goToEnteredPage(value: string): void {
    const page = Number(value);

    if (Number.isInteger(page) && page >= 1 && page <= this.totalPages) {
      this.goToPage(page);
      return;
    }

    this.cancelPageEdit();
  }

  startPageEdit(): void {
    this.isEditingPage = true;

    setTimeout(() => {
      if (this.pageNumberInput) {
        this.pageNumberInput.nativeElement.focus();
        this.pageNumberInput.nativeElement.select();
      }
    });
  }

  cancelPageEdit(): void {
    this.isEditingPage = false;
  }

  private openDeleteCategoryConfirmation(category: AdminBlogCategory): void {
    const title = 'Xóa danh mục';

    const description = 'Bạn có chắc chắn muốn xóa danh mục "' + category.name + '" không?';

    const waitDescription = 'Đang xóa danh mục...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.deleteCategory(category);
      });
  }

  private deleteCategory(category: AdminBlogCategory): void {
    this.deletingCategoryId = category.id;

    this.blogManagementDataService
      .deleteCategory(category.id)
      .pipe(
        take(1),
        finalize(() => {
          this.deletingCategoryId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể xóa danh mục'
            );

            return;
          }

          this.toastr.success('Xóa danh mục thành công');

          this.loadCategories();
        },

        (error) => {
          const errorResponse = error && error.error ? error.error : null;

          let message = 'Không thể xóa danh mục';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          } else if (error.status === 400 || error.status === 409) {
            message = 'Category còn bài viết hoặc danh mục con nên không thể xóa';
          }

          this.toastr.error(message);
        }
      );
  }
}
